<template>
  <v-col cols="12">
    <v-row align="center" dense class="mb-1">
      <v-col cols="auto">
        <v-select v-model="year" :label="$t('year')" :items="yearOptions" dense hide-details class="eudr-period-select"/>
      </v-col>
      <v-col cols="auto">
        <v-select v-model="month" :label="$t('month')" :items="monthOptions" item-text="text" item-value="value" dense hide-details class="eudr-period-select--month"/>
      </v-col>
      <v-spacer/>
      <v-col cols="auto">
        <Icon
          icon="mdi-refresh"
          :tooltipText="$t('refresh')"
          :small="false"
          elementId="account-eudr-refresh"
          @icon-clicked="loadReports"
        />
      </v-col>
    </v-row>

    <!-- Attach an external EUDR file (GeoJSON or tickets-grid CSV) for the selected period. -->
    <v-card outlined class="mb-4">
      <v-card-title class="subtitle-1 font-weight-medium">
        {{ $t('attachExternal') }}
        <Icon
          icon="mdi-information-outline"
          :tooltipText="$t('attachExternalInfo')"
          margin="ml-1"
          elementId="account-eudr-attach-info"
        />
      </v-card-title>
      <v-card-text>
        <EudrUploadPanel
          :accountId="account.accountId"
          :startDate="period.startDate"
          :endDate="period.endDate"
          @uploaded="loadReports"
        />
      </v-card-text>
    </v-card>

    <div class="subtitle-1 font-weight-medium mb-1">{{ $t('eudrRecords') }}</div>
    <DataTable
      :items="rows"
      :headers="headers"
      :loading="loading"
      :customCells="customColumns"
      tableKey="eudrBatchId"
      defaultSort="createdAt"
      :sortDesc="true"
      :showSearch="false"
      hideHeader
    >
      <template #status="{ item }">
        <div class="d-flex align-center justify-center">
          <EudrBatchStatusIcon :status="item.status"/>
          <Icon
            v-if="item.status === EudrBatchStatus.Error.value && item.errorSummary"
            icon="mdi-alert-circle-outline"
            iconColor="error"
            :tooltipText="item.errorSummary"
          />
        </div>
      </template>

      <template #actions="{ item }">
        <Icon
          icon="mdi-table-search"
          iconColor="primary"
          elementId="account-eudr-view"
          :tooltipText="$t('viewDetails')"
          :disabled="!hasUsableResult(item) || loadingDetails"
          @icon-clicked="openDetails(item)"
        />
        <Icon
          icon="mdi-download"
          iconColor="success"
          elementId="account-eudr-download"
          :tooltipText="$t('download')"
          :disabled="!hasUsableResult(item) || downloading"
          @icon-clicked="downloadReport(item)"
        />
        <Icon
          icon="mdi-delete"
          iconColor="error"
          elementId="account-eudr-delete"
          :tooltipText="$t('delete')"
          :disabled="deleting"
          @icon-clicked="promptDelete(item)"
        />
      </template>
    </DataTable>

    <Dialog :stateId="detailDialogId" @close="closeDetails" @click:outside="closeDetails" @dialog-closing="closeDetails">
      <EudrDetailDialog
        v-if="viewingDetails"
        :tracts="detailTracts"
        :featureCollection="detailFeatureCollection"
        :title="detailTitle"
        @close="closeDetails"
      />
    </Dialog>

    <Dialog :stateId="deleteDialogId" @close="cancelDelete" @click:outside="cancelDelete" @dialog-closing="cancelDelete">
      <ConfirmDelete
        v-if="reportToDelete"
        :title="$t('eudrPackage')"
        initialFocus="cancel"
        @delete="deleteSelectedReport"
        @cancel-delete="cancelDelete"
      />
    </Dialog>
  </v-col>
</template>

<script>
import { mapActions } from 'vuex'
import { saveAs } from 'file-saver'
import { EudrBatchStatus } from '@/utils/Enumerations.js'
import { utcToLocalDate } from '@/utils/DateFormatter.js'
import { uniqueDialogId } from '@/utils/componentHelpers'
import { eudrClient } from '@/utils/EudrClient.js'
import { loadReportDetail, hasUsableResult } from '@/utils/eudr/EudrDetail.js'
import { monthYearToRange, eudrYearOptions, eudrMonthOptions, windowEndDisplay } from '@/utils/eudr/EudrPeriod.js'

export default {
  name: 'AccountEudrTab',

  components: {
    DataTable: () => import('@/components/core/table/DataTable.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    EudrUploadPanel: () => import('@/components/eudr/EudrUploadPanel.vue'),
    EudrBatchStatusIcon: () => import('@/components/eudr/EudrBatchStatusIcon.vue'),
    EudrDetailDialog: () => import('@/components/eudr/EudrDetailDialog.vue'),
    ConfirmDelete: () => import('@/components/helper/ConfirmDelete.vue')
  },

  props: {
    account: { type: Object, required: true }
  },

  data () {
    const now = new Date()
    return {
      EudrBatchStatus,
      currentYear: now.getFullYear(),
      currentMonth: now.getMonth() + 1,
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      reports: [],
      loading: false,
      downloading: false,
      deleting: false,
      loadingDetails: false,
      viewingDetails: false,
      detailTracts: [],
      detailFeatureCollection: null,
      detailTitle: '',
      reportToDelete: null,
      detailDialogId: uniqueDialogId('account-eudr-detail-dialog'),
      deleteDialogId: uniqueDialogId('account-eudr-delete-dialog')
    }
  },

  computed: {
    yearOptions () {
      return eudrYearOptions(this.currentYear)
    },

    monthOptions () {
      return eudrMonthOptions(this.year, this.currentYear, this.currentMonth)
    },

    period () {
      return monthYearToRange(this.year, this.month)
    },

    rows () {
      return this.reports.map(r => ({ ...r, window: this.windowLabel(r) }))
    },

    headers () {
      return [
        { text: this.$t('created'), value: 'createdAt', sortable: true, align: 'start', format: v => (v ? utcToLocalDate(v, 'MM/DD/YYYY h:mm A') : this.$t('notAvailable')) },
        { text: this.$t('dateRange'), value: 'window', sortable: false, align: 'start' },
        { text: this.$t('eudrSourceFile'), value: 'sourceFileName', sortable: false, align: 'start', format: v => v || this.$t('notAvailable') },
        { text: this.$t('status'), value: 'status', sortable: false, align: 'center' },
        { text: this.$t('download'), value: 'actions', sortable: false, align: 'center' }
      ]
    },

    customColumns () {
      return [
        { slotName: 'status', value: 'status' },
        { slotName: 'actions', value: 'actions' }
      ]
    }
  },

  watch: {
    year () { this.loadReports() },
    month () { this.loadReports() }
  },

  created () {
    this.loadReports()
  },

  methods: {
    ...mapActions('eudr', ['fetchAccountBatches', 'deleteBatch']),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    hasUsableResult,

    windowLabel (report) {
      const start = report.startDate ? utcToLocalDate(report.startDate, 'MM/DD/YYYY') : ''
      const end = report.endDate ? windowEndDisplay(report.endDate) : ''
      return `${start} – ${end}`
    },

    async loadReports () {
      this.loading = true
      try {
        this.reports = await this.fetchAccountBatches({
          accountId: this.account.accountId,
          startDate: this.period.startDate,
          endDate: this.period.endDate
        })
      } catch (e) {
      } finally {
        this.loading = false
      }
    },

    async openDetails (item) {
      if (!hasUsableResult(item)) return
      this.loadingDetails = true
      try {
        const { tracts, featureCollection } = await loadReportDetail(item.resultBlobPath)
        this.detailTracts = tracts
        this.detailFeatureCollection = featureCollection
        this.detailTitle = `${this.account.name} — ${this.windowLabel(item)}`
        this.viewingDetails = true
        this.openOrUpdateDialog({ id: this.detailDialogId, width: '90vw' })
      } catch (e) {
        this.setSnackError(this.$t('eudrDownloadFailed'))
      } finally {
        this.loadingDetails = false
      }
    },

    closeDetails () {
      this.viewingDetails = false
      this.detailTracts = []
      this.detailFeatureCollection = null
      this.closeDialogsAtOrAbove(this.detailDialogId)
    },

    async downloadReport (item) {
      if (!hasUsableResult(item)) return
      this.downloading = true
      try {
        const blob = await eudrClient.fetchReportBlob(item.resultBlobPath)
        saveAs(blob, this.fileNameFor(item))
      } catch (e) {
        this.setSnackError(this.$t('eudrDownloadFailed'))
      } finally {
        this.downloading = false
      }
    },

    fileNameFor (item) {
      const name = this.account.name.replaceAll(/\s/g, '_').replaceAll(/[<>:"/\\|?*.]+/g, '_')
      const period = item.startDate ? utcToLocalDate(item.startDate, 'YYMM') : ''
      return `EUDR_${name}_${period}.json`
    },

    promptDelete (item) {
      this.reportToDelete = item
      this.openOrUpdateDialog({ id: this.deleteDialogId, width: '400px' })
    },

    cancelDelete () {
      this.reportToDelete = null
      this.closeDialogsAtOrAbove(this.deleteDialogId)
    },

    async deleteSelectedReport () {
      if (!this.reportToDelete) return
      this.deleting = true
      try {
        await this.deleteBatch(this.reportToDelete.eudrBatchId)
        this.cancelDelete()
        await this.loadReports()
      } catch (e) {
        // Keep the dialog open so the user can retry.
      } finally {
        this.deleting = false
      }
    }
  }
}
</script>

<style scoped>
/* Match the EUDR manager dialog's compact period selectors. */
.eudr-period-select {
  width: 110px;
}
/* "September" plus the dropdown arrow overflows the compact width and wraps the selection. */
.eudr-period-select--month {
  width: 150px;
}
</style>
